import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsToolsOptions } from '@onefootprint/fetch-dashboard';
import type { AgentTool } from '@onefootprint/request-types/dashboard';
import { Card, InlineAlert, Shimmer, Toggle } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toolSummary, VendorLogo } from '@/modules/agent';
import { enabledToolKeys, graphToolForCatalogKind, isToolAvailable, toolKey, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import useBuilderNavigation from '../../hooks/use-builder-navigation';
import { useBuilder } from '../../store/provider';
import ToolFilters, { activeToolFilters } from '../tool-filters';
import { findOrphanedToolMentions } from './utils/orphaned-tool-mentions';
const ToolsView = () => {
  const $ = _c(45);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.tools"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  const {
    filter,
    setFilter,
    query,
    setQuery
  } = useBuilderNavigation();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getOrgAgentsToolsOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const catalogQuery = useQuery(t1);
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== actions || $[3] !== catalogQuery || $[4] !== filter || $[5] !== isReadOnly || $[6] !== isSavingGraph || $[7] !== query || $[8] !== setFilter || $[9] !== setQuery || $[10] !== state.bundle || $[11] !== t) {
    const catalog = (catalogQuery.data?.tools ?? []).filter(isToolAvailable);
    const selectedKeys = enabledToolKeys(state.bundle.tools);
    const orphaned = findOrphanedToolMentions(state.bundle, catalog);
    let t8;
    if ($[19] !== actions || $[20] !== state.bundle.tools) {
      t8 = (catalogKind, enabled) => {
        const key = toolKeyForCatalogKind(catalogKind);
        const next = enabled ? [...state.bundle.tools, graphToolForCatalogKind(catalogKind)] : state.bundle.tools.filter(tool => toolKey(tool) !== key);
        actions.setTools(next);
      };
      $[19] = actions;
      $[20] = state.bundle.tools;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    const toggleTool = t8;
    const enabledCount = catalog.filter(tool_0 => selectedKeys.has(toolKeyForCatalogKind(tool_0.kind))).length;
    const active = activeToolFilters(catalog.length, filter, query);
    const needle = active.needle;
    const rows = catalog.map(tool_1 => ({
      tool: tool_1,
      enabled: selectedKeys.has(toolKeyForCatalogKind(tool_1.kind))
    })).sort(_temp).filter(t9 => {
      const {
        enabled: enabled_0
      } = t9;
      return active.filter === "all" || enabled_0 === (active.filter === "enabled");
    }).filter(t10 => {
      const {
        tool: tool_2
      } = t10;
      return !needle || `${tool_2.displayName} ${toolSummary(tool_2.kind, tool_2.description)}`.toLowerCase().includes(needle);
    });
    const renderTools = () => {
      if (catalogQuery.isPending) {
        return <div role="status" aria-label={t(_temp2)} className="flex flex-col gap-1">{[0, 1, 2].map(_temp3)}</div>;
      }
      if (catalogQuery.error) {
        return <InlineAlert variant="error" cta={{
          label: t(_temp4),
          onClick: () => void catalogQuery.refetch()
        }}>{t(_temp5)}</InlineAlert>;
      }
      if (catalog.length === 0) {
        return <Card.Empty>{t(_temp6)}</Card.Empty>;
      }
      if (rows.length === 0) {
        const message = needle ? t(_temp7) : active.filter === "enabled" ? t(_temp8) : t(_temp9);
        return <Card.Empty role="status">{message}</Card.Empty>;
      }
      return <Card.List aria-label={t(_temp10)}>{rows.map(t11 => {
          const {
            tool: tool_3,
            enabled: enabled_1
          } = t11;
          return <Card.Body as="li" key={tool_3.kind} className="flex items-center gap-4 px-4 py-3"><VendorLogo toolKind={tool_3.kind} name={tool_3.displayName} size="default" /><div className="flex min-w-0 flex-1 flex-col gap-1"><p className="text-label-2 text-primary">{tool_3.displayName}</p><p className="text-body-3 text-tertiary">{toolSummary(tool_3.kind, tool_3.description)}</p></div><Toggle aria-label={tool_3.displayName} checked={enabled_1} disabled={isReadOnly || isSavingGraph} onChange={value => toggleTool(tool_3.kind, value)} /></Card.Body>;
        })}</Card.List>;
    };
    t6 = "max-w-[800px] mx-auto px-6 py-8 sm:px-10 flex flex-col gap-8";
    let t12;
    if ($[22] !== t) {
      t12 = t(_temp11);
      $[22] = t;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== t12) {
      t13 = <h2 className="text-heading-2 text-primary">{t12}</h2>;
      $[24] = t12;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== t) {
      t14 = t(_temp12);
      $[26] = t;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t14) {
      t15 = <p className="text-body-2 text-tertiary">{t14}</p>;
      $[28] = t14;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    if ($[30] !== t13 || $[31] !== t15) {
      t7 = <div className="flex flex-col gap-1">{t13}{t15}</div>;
      $[30] = t13;
      $[31] = t15;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    t3 = "@container flex flex-col gap-6";
    t4 = orphaned.length > 0 && <InlineAlert variant="warning">{orphaned.length === 1 ? t(_temp13, {
        names: orphaned[0].displayName
      }) : t(_temp14, {
        names: orphaned.map(_temp15).join(", ")
      })}</InlineAlert>;
    t5 = active.canFilter && <ToolFilters filter={filter} query={query} enabledCount={enabledCount} onFilterChange={setFilter} onQueryChange={setQuery} label={t(_temp16)} searchLabel={t(_temp17)} />;
    T0 = Card.Root;
    t2 = renderTools();
    $[2] = actions;
    $[3] = catalogQuery;
    $[4] = filter;
    $[5] = isReadOnly;
    $[6] = isSavingGraph;
    $[7] = query;
    $[8] = setFilter;
    $[9] = setQuery;
    $[10] = state.bundle;
    $[11] = t;
    $[12] = T0;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    T0 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
  }
  let t8;
  if ($[33] !== T0 || $[34] !== t2) {
    t8 = <T0>{t2}</T0>;
    $[33] = T0;
    $[34] = t2;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t3 || $[37] !== t4 || $[38] !== t5 || $[39] !== t8) {
    t9 = <section className={t3}>{t4}{t5}{t8}</section>;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t6 || $[42] !== t7 || $[43] !== t9) {
    t10 = <div className={t6}>{t7}{t9}</div>;
    $[41] = t6;
    $[42] = t7;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  return t10;
};
export default ToolsView;
function _temp(a, b) {
  return a.tool.displayName.localeCompare(b.tool.displayName);
}
function _temp2($) {
  return $.loading;
}
function _temp3(index) {
  return <Card.Body key={index} className="flex gap-4 px-4 py-3"><Shimmer className="size-8 shrink-0 rounded-md" /><div className="flex flex-1 flex-col gap-2"><Shimmer height={16} width="30%" /><Shimmer height={16} width="75%" /></div></Card.Body>;
}
function _temp4($_0) {
  return $_0.retry;
}
function _temp5($_1) {
  return $_1.error;
}
function _temp6($_2) {
  return $_2.emptyState;
}
function _temp7($_3) {
  return $_3.noMatches;
}
function _temp8($_4) {
  return $_4.noneEnabled;
}
function _temp9($_5) {
  return $_5.allEnabled;
}
function _temp10($_6) {
  return $_6.list;
}
function _temp11($_7) {
  return $_7.title;
}
function _temp12($_8) {
  return $_8.subtitle;
}
function _temp13($_9) {
  return $_9.orphaned_one;
}
function _temp14($_10) {
  return $_10.orphaned_other;
}
function _temp15(tool_4) {
  return tool_4.displayName;
}
function _temp16($_11) {
  return $_11.filters.label;
}
function _temp17($_12) {
  return $_12.search;
}